import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Play, Copy, Check, Terminal, Radio, AlertTriangle, CornerDownLeft, Smartphone } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/misc';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useT } from '@/lib/i18n';
import { apiBase, apiFetch, type RawResult } from '@/lib/api';
import { useFreeQuotaToast } from '@/lib/free-quota';
import { track } from '@/lib/analytics';
import { brPhoneForms, formatPhone, samePhone } from '@/lib/phone';
import { PhoneInput } from '@/components/PhoneInput';
import { normalizeLid, type DestKind } from '@/lib/destination';
import { WebhookInspector } from '@/components/WebhookInspector';
import { MetaEventsPanel } from '@/components/MetaEventsPanel';
import { useProjects } from '@/components/ProjectSwitcher';
import { getProject } from '@/lib/api';
import { isOfficial } from '@/lib/projects';
import { availableOn } from '@/lib/rails';
import { PlaygroundTemplatePicker, DestinationWindow, WindowGate } from '@/components/PlaygroundTemplate';
import { PlaygroundLive } from '@/components/PlaygroundLive';
import { PlaygroundTester } from '@/components/PlaygroundTester';
import { isTesterType } from '@/lib/message-tester';
import { useDestinationWindow, useFallbackName } from '@/lib/window';

interface PgInstance { id: string; phone: string; nickname?: string; status: string }
import {
  CATEGORIES, OPERATIONS, OTP_PREREQ_OFFICIAL, SEND_PREREQ, SEND_PREREQ_OFFICIAL, SNIPPET_LABEL, buildUrl, snippet, type Operation, type SnippetLang,
} from '@/lib/playground-catalog';

// Grupos de mensagem do projeto oficial (ver ops).
const OFFICIAL_START = 'Iniciar conversa';
const OFFICIAL_REPLY = 'Responder (janela de 24h)';

const LANGS: SnippetLang[] = ['curl', 'node', 'python', 'php', 'dotnet', 'java', 'go', 'ruby'];

// richNodes renderiza um mini-markdown inline: `código`, **negrito** e [texto](url).
function richNodes(text: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  const re = /`([^`]+)`|\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)]+)\)/g;
  let last = 0;
  let k = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(<span key={k++}>{text.slice(last, m.index)}</span>);
    if (m[1] != null) {
      nodes.push(<code key={k++} className="rounded bg-muted px-1 py-0.5 text-xs text-foreground">{m[1]}</code>);
    } else if (m[2] != null) {
      nodes.push(<strong key={k++} className="font-semibold text-foreground">{m[2]}</strong>);
    } else {
      nodes.push(<a key={k++} href={m[4]} target="_blank" rel="noreferrer" className="text-primary underline underline-offset-2">{m[3]}</a>);
    }
    last = re.lastIndex;
  }
  if (last < text.length) nodes.push(<span key={k++}>{text.slice(last)}</span>);
  return nodes;
}

function Guide({ text }: { text: string }) {
  return <p className="text-sm leading-relaxed text-muted-foreground">{richNodes(text)}</p>;
}

function methodColor(m: string) {
  return m === 'GET' ? 'text-[#3c9a18]' : m === 'DELETE' ? 'text-destructive' : m === 'PATCH' ? 'text-[#b5620a]' : 'text-primary';
}

export function Playground() {
  const t = useT();
  const onFreeQuota = useFreeQuotaToast();
  // Só o que existe no trilho do projeto: no oficial, presença, grupos, QR e
  // afins responderiam 409 — oferecer isso é ensinar o que não funciona.
  const { data: projects = [] } = useProjects();
  const activeProject = projects.find((p) => p.id === getProject()) ?? projects.find((p) => p.is_default);
  const official = isOfficial(activeProject);
  const ops = useMemo(() => {
    const list = OPERATIONS.filter((o) => availableOn(__BZ_RAILS__, o.method, o.path, official ? 'official' : 'unofficial'));
    if (!official) return list;
    // No oficial, mensagem tem dois papéis diferentes: o template INICIA conversa;
    // o resto só RESPONDE dentro da janela de 24h. Misturar os dois ensinava a
    // tentar abrir conversa com texto — e a Meta recusa. O OTP fica com os que
    // iniciam: fora da janela ele sai pelo template de autenticação do projeto.
    return list
      .map((o) => ({
        ...o,
        guide: o.officialGuide ?? o.guide,
        sample: o.officialSample ?? o.sample,
        category: o.category !== 'Mensagens' ? o.category : (o.id === 'sendTemplate' || o.id === 'otp') ? OFFICIAL_START : OFFICIAL_REPLY,
      }));
  }, [official]);
  const cats = useMemo(
    () => (official ? [OFFICIAL_START, OFFICIAL_REPLY, ...CATEGORIES] : [...CATEGORIES])
      .filter((c) => ops.some((o) => o.category === c)),
    [ops, official],
  );
  const [opId, setOpId] = useState<string>(OPERATIONS[0].id);
  // A operação como a tela a mostra (no oficial, com guia e exemplo do oficial);
  // `baseOp` é a do catálogo, para as regras que dependem da categoria original.
  const op = useMemo(() => ops.find((o) => o.id === opId) ?? OPERATIONS.find((o) => o.id === opId) ?? OPERATIONS[0], [opId, ops]);
  const baseOp = useMemo(() => OPERATIONS.find((o) => o.id === opId) ?? OPERATIONS[0], [opId]);
  const prereq = official && op.id === 'otp' ? OTP_PREREQ_OFFICIAL
    : official && op.prereq === SEND_PREREQ ? SEND_PREREQ_OFFICIAL : op.prereq;
  // No oficial, o erro que mais aparece nos envios livres é a janela fechada.
  const errors = official && op.id === 'otp'
    ? [{ code: 'official_otp_template_required', status: 422, when: 'janela de 24 horas fechada e nenhum template de autenticação aprovado no projeto' },
       { code: 'official_otp_template_invalid', status: 422, when: '`template` informado não é de autenticação ou não está aprovado' }, ...(op.errors ?? [])]
    : official && baseOp.category === 'Mensagens' && op.id !== 'sendTemplate'
    ? [{ code: 'official_needs_template', status: 422, when: 'janela de 24 horas fechada e nenhum template de alternativa no projeto' }, ...(op.errors ?? [])]
    : op.errors;

  const [params, setParams] = useState<Record<string, string>>({});
  const [payload, setPayload] = useState(() => {
    const s = OPERATIONS[0].sample;
    if (s && typeof s === 'object' && 'to' in s) return JSON.stringify({ ...s, to: '' }, null, 2);
    return JSON.stringify(s ?? {}, null, 2);
  });
  const [lang, setLang] = useState<SnippetLang>('curl');
  const [resp, setResp] = useState<RawResult | null>(null);
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);

  // Padrões globais aplicados a TODOS os exemplos (no topo). O destino fica
  // guardado por projeto: recarregar a página (ou a versão nova do painel) não
  // pode apagar com quem se estava conversando.
  const destKey = `bz_pg_dest_${getProject()}`;
  const [testPhone, setTestPhone] = useState<string>(() => {
    try { return localStorage.getItem(destKey) ?? ''; } catch { return ''; }
  });
  const [testInstance, setTestInstance] = useState<string>('');
  // Destino por telefone ou por @lid: o @lid é o identificador de privacidade
  // que a API devolve em `sender.lid` e nos participantes de grupo, e às vezes é
  // o ÚNICO endereço que se tem da pessoa. O envio aceita os dois, então o
  // playground também — senão não há como TESTAR o caminho do @lid.
  const [destKind, setDestKind] = useState<DestKind>('phone');
  const [lidRaw, setLidRaw] = useState<string>('');

  const { data: instances = [] } = useQuery<PgInstance[]>({
    queryKey: ['pg-instances'],
    queryFn: async () => ((await apiFetch('GET', '/instances')).data as { data?: PgInstance[] })?.data ?? [],
    refetchInterval: 10000,
  });

  // Janela de 24h com o destino (oficial): texto e mídia só chegam com ela aberta.
  const destWin = useDestinationWindow(testPhone);

  // Quem acabou de escrever para o número (oficial): o teste natural é responder
  // essa pessoa. Destino vazio é preenchido com ela; outro destino ganha a oferta
  // de trocar. Sem isso, a conversa começava e o Playground não deixava seguir.
  const { data: recentConvs = [] } = useQuery({
    queryKey: ['conversations', ''],
    enabled: official,
    refetchInterval: 5000,
    queryFn: async () => ((await apiFetch('GET', '/conversations')).data as { data?: { chat_jid: string; name?: string; last_inbound_at?: string }[] })?.data ?? [],
  });
  const latest = useMemo(() => {
    const open = recentConvs
      .filter((c) => c.last_inbound_at && c.chat_jid.endsWith('@s.whatsapp.net')
        && Date.now() - new Date(c.last_inbound_at).getTime() < 24 * 3600 * 1000)
      .sort((a, b) => (b.last_inbound_at ?? '').localeCompare(a.last_inbound_at ?? ''));
    const c = open[0];
    if (!c) return null;
    // A forma atual do celular (com o 9), que é como a pessoa reconhece o número.
    const forms = brPhoneForms(c.chat_jid.split('@')[0]);
    const phone = '+' + (forms.find((f) => f.length === 13) ?? forms[0]);
    return { phone, label: c.name ? `${c.name} (${formatPhone(phone)})` : formatPhone(phone), at: c.last_inbound_at! };
  }, [recentConvs]);
  const [autoFilled, setAutoFilled] = useState<string | null>(null);
  useEffect(() => {
    if (!official || !latest || testPhone.trim() || destKind !== 'phone') return;
    applyPhone(latest.phone);
    setAutoFilled(latest.label);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [official, latest?.phone]);
  const sameDest = !!latest && samePhone(testPhone, latest.phone);
  const offerLatest = official && latest && !sameDest && Date.now() - new Date(latest.at).getTime() < 30 * 60 * 1000;
  const fallbackName = useFallbackName(official);
  // O OTP decide a janela sozinho (texto dentro, template de autenticação fora).
  const freeForm = baseOp.category === 'Mensagens' && op.id !== 'sendTemplate' && op.id !== 'otp';
  const windowClosed = official && freeForm && destWin.known && !destWin.open;
  const windowBlocked = windowClosed && !fallbackName;
  // O número do projeto, como o contato o vê no topo da conversa (prévia).
  const sender = instances.find((i) => i.id === testInstance) ?? instances[0];
  const businessName = sender ? (sender.nickname || formatPhone(sender.phone)) : undefined;

  // Select reutilizável de número (instance_id).
  const instanceSelect = (value: string, onChange: (v: string) => void) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="font-mono text-xs">
        <SelectValue placeholder={instances.length ? t('playground.selectConnected') : t('playground.noConnected')} />
      </SelectTrigger>
      <SelectContent>
        {instances.map((i) => (
          <SelectItem key={i.id} value={i.id}>
            <span className="flex items-center gap-2"><Smartphone className="size-3.5" /> {i.nickname || formatPhone(i.phone)} · {i.status}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const bodyObj = useMemo(() => { try { return JSON.parse(payload) as Record<string, unknown>; } catch { return null; } }, [payload]);

  // Onde o número (instância) entra nesta operação: param (instance_id/id) ou body.
  const opInstanceParam = (o: Operation) => (o.params ?? []).find((p) => p.name === 'instance_id' || p.name === 'id')?.name;
  const opTakesBodyInstance = (o: Operation) =>
    o.sample !== undefined && o.sample !== null && typeof o.sample === 'object'
    && (o.category === 'Mensagens' || 'instance_id' in (o.sample as object)) && !opInstanceParam(o);

  // Aplica os padrões globais (telefone/instância) a um sample ao trocar de operação.
  const withDefaults = (next: Operation): string => {
    if (next.sample === undefined) return '';
    const body = JSON.parse(JSON.stringify(next.sample));
    if (body && typeof body === 'object') {
      if ('to' in body) body.to = testPhone;
      if (testInstance && opTakesBodyInstance(next)) body.instance_id = testInstance;
    }
    return JSON.stringify(body, null, 2);
  };

  const applyPhone = (phone: string) => {
    setTestPhone(phone);
    try { if (phone && !phone.includes('@lid')) localStorage.setItem(destKey, phone); else localStorage.removeItem(destKey); } catch { /* sem storage: só não lembra */ }
    try { const o = JSON.parse(payload); if (o && typeof o === 'object' && 'to' in o) { o.to = phone; setPayload(JSON.stringify(o, null, 2)); } } catch { /* ignore */ }
  };

  const applyLid = (raw: string) => {
    setLidRaw(raw.trim());
    applyPhone(normalizeLid(raw));
  };

  // Trocar de tipo limpa o destino: telefone e @lid não se convertem um no outro.
  const switchDestKind = (k: DestKind) => {
    if (k === destKind) return;
    setDestKind(k);
    setLidRaw('');
    applyPhone('');
  };
  const applyInstance = (id: string) => {
    setTestInstance(id);
    const ip = opInstanceParam(op);
    if (ip) { setParams((s) => ({ ...s, [ip]: id })); return; }
    if (opTakesBodyInstance(op)) {
      try { const o = JSON.parse(payload); if (o && typeof o === 'object') { o.instance_id = id; setPayload(JSON.stringify(o, null, 2)); } } catch { /* ignore */ }
    }
  };

  const selectOp = (next: Operation) => {
    setOpId(next.id);
    const ip = opInstanceParam(next);
    setParams(ip && testInstance ? { [ip]: testInstance } : {});
    setPayload(withDefaults(next));
    setResp(null);
  };

  // No oficial, o que mais se testa é template: o Playground já abre nele (uma vez,
  // sem atropelar a escolha de quem já mudou de operação).
  const [openedOfficial, setOpenedOfficial] = useState(false);
  useEffect(() => {
    if (!official || openedOfficial) return;
    setOpenedOfficial(true);
    const tplOp = ops.find((o) => o.id === 'sendTemplate');
    if (tplOp && opId === OPERATIONS[0].id) selectOp(tplOp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [official]);

  // Sessão: traz a primeira encontrada já selecionada (preferindo conectada);
  // o usuário troca se quiser.
  useEffect(() => {
    if (testInstance || instances.length === 0) return;
    const first = instances.find((i) => i.status === 'connected' || i.status === 'warming') ?? instances[0];
    applyInstance(first.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instances]);

  // Esta operação exige um telefone (`to` no corpo)?
  const needsPhone = op.sample !== null && typeof op.sample === 'object' && 'to' in (op.sample as object);
  const phoneMissing = needsPhone && !testPhone.trim();

  const send = async () => {
    let body: unknown;
    if (op.sample !== undefined) {
      try { body = JSON.parse(payload); } catch { toast.error(t('playground.invalidJson')); return; }
    }
    for (const p of op.params ?? []) {
      if (p.required && !(params[p.name] ?? '').trim()) { toast.error(t('playground.fillParam', { name: p.name })); return; }
    }
    const path = buildUrl(op, '', params);
    setSending(true);
    track('playground_request', { operation: op.id, method: op.method });
    try {
      const r = await apiFetch(op.method, path, body);
      setResp(r);
      // 402 da franquia do Free: mensagem do servidor + upgrade (a resposta crua
      // continua no painel — aqui é uma ferramenta de desenvolvedor).
      onFreeQuota(r);
    } finally { setSending(false); }
  };

  const code = snippet(op, apiBase(), lang, op.sample !== undefined ? (bodyObj ?? undefined) : undefined);
  const copyCode = () => { navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  return (
    <div className="flex flex-col gap-5 xl:flex-row xl:items-start">
      <div className="flex min-w-0 flex-1 flex-col gap-5">
      {/* Padrões globais: telefone + número (instância) aplicados a todos os exemplos. */}
      <Card>
        <CardContent className="flex flex-col gap-4 p-4 sm:flex-row sm:items-start">
          <div className="flex flex-1 flex-col gap-1.5">
            <div className="flex items-center justify-between gap-2">
              <Label>{t('playground.destination')}</Label>
              {!official && <div className="flex overflow-hidden rounded-md border border-input text-xs">
                {(['phone', 'lid'] as const).map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => switchDestKind(k)}
                    className={cn('px-2 py-1 transition-colors', destKind === k
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-muted')}
                  >
                    {t(k === 'phone' ? 'playground.destKindPhone' : 'playground.destKindLid')}
                  </button>
                ))}
              </div>}
            </div>
            {destKind === 'phone'
              ? <PhoneInput value={testPhone} onChange={applyPhone} />
              : <Input value={lidRaw} onChange={(e) => applyLid(e.target.value)} placeholder="99887766@lid" className="h-9 font-mono text-xs" />}
            {official && <DestinationWindow phone={testPhone} />}
            {official && autoFilled && sameDest && (
              <p className="text-xs text-emerald-700 dark:text-emerald-400">{t('playground.autoDest', { name: autoFilled })}</p>
            )}
            {offerLatest && (
              <button type="button" onClick={() => { applyPhone(latest!.phone); setAutoFilled(latest!.label); }}
                className="self-start rounded-md border border-emerald-600/40 bg-emerald-500/10 px-2 py-1 text-xs text-emerald-800 hover:bg-emerald-500/20 dark:text-emerald-300">
                {t('playground.useLatest', { name: latest!.label })}
              </button>
            )}
            <p className="text-xs text-muted-foreground">
              {destKind === 'phone' ? t('playground.testPhoneNote', { code: 'to' }) : richNodes(t('playground.destLidNote'))}
              {destKind === 'lid' && testPhone && testPhone !== lidRaw && (
                <> <code className="rounded bg-muted px-1 py-0.5 text-foreground">{testPhone}</code></>
              )}
            </p>
          </div>
          <div className="flex flex-1 flex-col gap-1.5">
            <Label>{t('playground.sendNumber')}</Label>
            {instanceSelect(testInstance, applyInstance)}
            <p className="text-xs text-muted-foreground">{t('playground.sendNumberNote')}</p>
          </div>
        </CardContent>
      </Card>

      {official && (
        <PlaygroundLive
          businessPhone={sender?.phone}
          testPhone={testPhone}
          instanceId={testInstance}
        />
      )}

      <div className="grid gap-6 lg:grid-cols-[230px_1fr]">
      {/* Catálogo (lista no desktop; no mobile vira dropdown abaixo) */}
      <aside className="hidden flex-col gap-4 lg:flex">
        {cats.map((cat) => (
          <div key={cat}>
            <p className="mb-1 px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{cat}</p>
            <div className="flex flex-col">
              {ops.filter((o) => o.category === cat).map((o) => (
                <button
                  key={o.id}
                  onClick={() => selectOp(o)}
                  className={cn('flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
                    o.id === opId ? 'bg-primary/12 font-medium text-primary' : 'text-muted-foreground hover:bg-accent hover:text-foreground')}
                >
                  <span className={cn('w-11 shrink-0 font-mono text-[10px] font-bold', methodColor(o.method))}>{o.method}</span>
                  <span className="truncate">{o.title}</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </aside>

      {/* Detalhe */}
      <div className="flex min-w-0 flex-col gap-5">
        {/* Mobile: seletor de operação (a lista lateral só aparece no desktop). */}
        <select
          value={opId}
          onChange={(e) => { const o = OPERATIONS.find((x) => x.id === e.target.value); if (o) selectOp(o); }}
          className="h-10 w-full rounded-md border bg-card px-3 text-sm lg:hidden"
          aria-label={t('playground.operationAria')}
        >
          {cats.map((cat) => (
            <optgroup key={cat} label={cat}>
              {ops.filter((o) => o.category === cat).map((o) => (
                <option key={o.id} value={o.id}>{o.method} · {o.title}</option>
              ))}
            </optgroup>
          ))}
        </select>

        <div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className={cn('font-mono', methodColor(op.method))}>{op.method}</Badge>
            <code className="text-sm text-muted-foreground">{op.path}</code>
            {op.stream && <Badge variant="warning" className="gap-1"><Radio className="size-3" /> SSE</Badge>}
          </div>
          <h2 className="mt-2 text-xl font-bold">{op.title}</h2>
          <div className="mt-1"><Guide text={op.guide} /></div>
        </div>

        {/* Pré-requisito */}
        {prereq && (
          <div className="flex items-start gap-2 rounded-md border border-[#ffb020]/40 bg-[#ffb020]/10 px-4 py-3 text-sm">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-[#b5620a]" />
            <span className="text-foreground/90">{prereq}</span>
          </div>
        )}

        {/* Retornos esperados */}
        {op.returns && (
          <div className="flex items-start gap-2 text-sm">
            <CornerDownLeft className="mt-0.5 size-4 shrink-0 text-[#3c9a18]" />
            <span><span className="font-semibold">{t('playground.returnsLabel')}</span><span className="text-muted-foreground">{richNodes(op.returns)}</span></span>
          </div>
        )}

        {/* Erros comuns */}
        {errors && errors.length > 0 && (
          <div>
            <p className="mb-2 text-sm font-semibold">{t('playground.commonErrors')}</p>
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full min-w-md text-sm">
                <tbody>
                  {errors.map((e) => (
                    <tr key={e.code} className="border-b last:border-0">
                      <td className="px-3 py-2"><Badge variant={e.status >= 500 ? 'destructive' : 'warning'} className="font-mono text-xs">{e.status}</Badge></td>
                      <td className="px-3 py-2"><code className="text-xs font-semibold text-foreground">{e.code}</code></td>
                      <td className="px-3 py-2 text-muted-foreground">{e.when}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-1.5 text-xs text-muted-foreground">{t('playground.useCodeNote', { code: 'code' })}</p>
          </div>
        )}

        {/* Requisição */}
        {!op.stream && (
          <Card>
            <CardContent className="flex flex-col gap-4 p-5">
              {(op.params ?? []).map((p) => {
                const isInstance = p.name === 'instance_id' || p.name === 'id';
                return (
                  <div key={p.name} className="flex flex-col gap-2">
                    <Label htmlFor={p.name}>
                      {p.name} <span className="text-xs font-normal text-muted-foreground">({p.in}{p.required ? `, ${t('playground.paramRequired')}` : ''}) {p.hint}</span>
                    </Label>
                    {isInstance
                      ? instanceSelect(params[p.name] ?? '', (v) => setParams((s) => ({ ...s, [p.name]: v })))
                      : <Input id={p.name} value={params[p.name] ?? ''} onChange={(e) => setParams((s) => ({ ...s, [p.name]: e.target.value }))} placeholder={p.sample} className="font-mono text-xs" />}
                  </div>
                );
              })}
              {windowClosed && (
                <WindowGate
                  blocked={windowBlocked}
                  fallbackName={fallbackName}
                  onUseTemplate={() => { const o = ops.find((x) => x.id === 'sendTemplate'); if (o) selectOp(o); }}
                />
              )}
              {op.id === 'sendTemplate' && <PlaygroundTemplatePicker payload={payload} onPayload={setPayload} businessName={businessName} />}
              {official && isTesterType(op.id) && (
                <PlaygroundTester type={op.id} payload={payload} onPayload={setPayload} businessName={businessName} />
              )}
              {op.sample !== undefined && (
                <div className="flex flex-col gap-2">
                  <Label htmlFor="payload">{t('playground.payloadLabel')}</Label>
                  <textarea
                    id="payload" value={payload} onChange={(e) => setPayload(e.target.value)} spellCheck={false}
                    rows={Math.min(18, payload.split('\n').length + 1)}
                    className="w-full rounded-md border border-input bg-transparent p-3 font-mono text-xs leading-relaxed outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/40"
                  />
                </div>
              )}
              <div className="flex items-center gap-3">
                <Button onClick={send} disabled={sending || phoneMissing || windowBlocked}>
                  {sending ? <Spinner className="border-primary-foreground/40 border-t-primary-foreground" /> : <Play className="size-4" />}
                  {t('playground.sendRequest')}
                </Button>
                {phoneMissing && <span className="text-sm text-muted-foreground">{t('playground.phoneMissing')}</span>}
                {resp && (
                  <span className="flex items-center gap-2 text-sm">
                    <Badge variant={resp.ok ? 'success' : 'destructive'}>{resp.status || t('playground.errorBadge')}</Badge>
                    <span className="text-muted-foreground">{resp.durationMs} ms</span>
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {op.stream && (
          <Card><CardContent className="flex items-center gap-3 p-5 text-sm text-muted-foreground">
            <Radio className="size-5 shrink-0 text-primary" />
            {t('playground.streamNote', { code: 'EventSource' })}
          </CardContent></Card>
        )}

        {resp && !op.stream && (
          <div>
            <p className="mb-2 text-sm font-semibold">{t('playground.responseLabel')}</p>
            <pre className="max-h-80 overflow-auto rounded-md border bg-muted/50 p-4 text-xs leading-relaxed">{JSON.stringify(resp.data, null, 2)}</pre>
          </div>
        )}

        {/* Exemplos */}
        <div>
          <div className="mb-2 flex items-center gap-2"><Terminal className="size-4 text-muted-foreground" /><p className="text-sm font-semibold">{t('playground.examplesTitle')}</p></div>
          <Tabs value={lang} onValueChange={(v) => setLang(v as SnippetLang)}>
            <div className="flex items-center justify-between">
              <TabsList>{LANGS.map((l) => <TabsTrigger key={l} value={l}>{SNIPPET_LABEL[l]}</TabsTrigger>)}</TabsList>
              <Button variant="ghost" size="sm" onClick={copyCode}>{copied ? <Check className="size-4 text-[#3c9a18]" /> : <Copy className="size-4" />} {t('playground.copy')}</Button>
            </div>
            {LANGS.map((l) => (
              <TabsContent key={l} value={l}>
                <pre className="overflow-auto rounded-md border bg-muted/50 p-4 text-xs leading-relaxed">{snippet(op, apiBase(), l, op.sample !== undefined ? (bodyObj ?? undefined) : undefined)}</pre>
              </TabsContent>
            ))}
          </Tabs>
          <p className="mt-2 text-xs text-muted-foreground">{t('playground.sdkNote')}</p>
        </div>
      </div>
      </div>
      {/* O lado Meta → bZapper dos webhooks (só no oficial). */}
      {official && <MetaEventsPanel />}
      </div>

      {/* Inspector ao vivo: em telas grandes, coluna fixa à direita (altura total). */}
      <div className="w-full xl:sticky xl:top-16 xl:h-[calc(100vh-5rem)] xl:w-lg 2xl:w-2xl xl:shrink-0">
        <WebhookInspector fullHeight official={official} />
      </div>
    </div>
  );
}
